import React from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

const ServiceCTA = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const pathname = location.pathname

  const isTranscriptomics = pathname === '/service/bioinformatics/transcriptomics'
  const isEpigenomics = pathname === '/service/bioinformatics/epigenomics'
  const isMassSpec = pathname === '/service/bioinformatics/mass-spectrometry'
  const isMicrobiome = pathname === '/service/bioinformatics/microbiome'
  const isGenomics = pathname === '/service/bioinformatics/genomics'
  const isSingleCell = pathname === '/service/bioinformatics/single-cell'
  const isSpatialOmics = pathname === '/service/bioinformatics/spatial-omics'
  const isMultiOmics = pathname === '/service/bioinformatics/multi-omics'
  const isPipelineDev = pathname === '/service/bioinformatics/pipeline-development'
  const isPublicDataMining = pathname === '/service/bioinformatics/public-data-mining'
  const isBioinformatics = pathname.startsWith('/service/bioinformatics')
  const isAiMl = pathname.startsWith('/service/ai-ml')
  const isAiMlOverview = pathname === '/service/ai-ml'
  const isTargetIdentification = pathname === '/service/ai-ml/target-identification'
  const isSurvivalRisk = pathname === '/service/ai-ml/survival-risk'
  const isBiomarkerDiscovery = pathname === '/service/ai-ml/biomarker-discovery'
  const isPreclinicalBiomarker = pathname === '/service/ai-ml/preclinical-biomarker'
  const isTranslationalPkPd = pathname === '/service/ai-ml/translational-pkpd'
  const isLeadOptimization = pathname === '/service/ai-ml/lead-optimization'
  const isHitDiscovery = pathname === '/service/ai-ml/hit-discovery'
  const isDrugDiscoveryAiMl = pathname.startsWith('/service/ai-ml/drug-discovery')
  const isAppliedAiMl = pathname.startsWith('/service/ai-ml/applied')
  const clinicalResearchAiMlPaths = [
    '/service/ai-ml/clinical-research',
    '/service/ai-ml/patient-stratification',
    '/service/ai-ml/survival-risk',
    '/service/ai-ml/biomarker-discovery',
    '/service/ai-ml/cohort-reanalysis'
  ]
  const isClinicalResearchAiMl = clinicalResearchAiMlPaths.some((basePath) => pathname.startsWith(basePath))
  const isClinicalResearch = pathname === '/service/ai-ml/clinical-research'
  const isPatientStratification = pathname === '/service/ai-ml/patient-stratification'
  const isCohortReanalysis = pathname === '/service/ai-ml/cohort-reanalysis'
  const isConsultancy = pathname.startsWith('/service/consultancy')
  const isScientificWriting = pathname.startsWith('/service/scientific-writing')
  const isDataSecurity = pathname.startsWith('/service/data-security')

  let titleKey = 'technicalService.cta.title'
  let descKey = 'technicalService.cta.desc'

  if (isTranscriptomics) {
    titleKey = 'technicalService.cta.bioinformatics.title'
    descKey = 'technicalService.cta.bioinformatics.desc'
  } else if (isEpigenomics) {
    titleKey = 'technicalService.cta.epigenomics.title'
    descKey = 'technicalService.cta.epigenomics.desc'
  } else if (isMassSpec) {
    titleKey = 'technicalService.cta.bioinformatics.title'
    descKey = 'technicalService.cta.bioinformatics.desc'
  } else if (isMicrobiome) {
    titleKey = 'technicalService.cta.bioinformatics.title'
    descKey = 'technicalService.cta.bioinformatics.desc'
  } else if (isGenomics) {
    titleKey = 'technicalService.cta.bioinformatics.title'
    descKey = 'technicalService.cta.bioinformatics.desc'
  } else if (isSingleCell) {
    titleKey = 'technicalService.cta.bioinformatics.title'
    descKey = 'technicalService.cta.bioinformatics.desc'
  } else if (isSpatialOmics) {
    titleKey = 'technicalService.cta.bioinformatics.title'
    descKey = 'technicalService.cta.bioinformatics.desc'
  } else if (isMultiOmics) {
    titleKey = 'technicalService.cta.bioinformatics.title'
    descKey = 'technicalService.cta.bioinformatics.desc'
  } else if (isPipelineDev) {
    titleKey = 'technicalService.cta.bioinformatics.title'
    descKey = 'technicalService.cta.bioinformatics.desc'
  } else if (isPublicDataMining) {
    titleKey = 'technicalService.cta.bioinformatics.title'
    descKey = 'technicalService.cta.bioinformatics.desc'
  } else if (isBioinformatics) {
    titleKey = 'technicalService.cta.bioinformatics.title'
    descKey = 'technicalService.cta.bioinformatics.desc'
  } else if (isClinicalResearchAiMl) {
    titleKey = 'technicalService.cta.clinicalResearch.title'
    descKey = 'technicalService.cta.clinicalResearch.desc'
  } else if (isAppliedAiMl) {
    titleKey = 'technicalService.cta.aiMl.desc'
    descKey = ''
  } else if (isDrugDiscoveryAiMl) {
    titleKey = ''
    descKey = ''
  } else if (isAiMl) {
    titleKey = 'technicalService.cta.aiMl.title'
    descKey = 'technicalService.cta.aiMl.desc'
  } else if (isConsultancy) {
    titleKey = 'technicalService.cta.consultancy.title'
    descKey = 'technicalService.cta.consultancy.desc'
  } else if (isScientificWriting) {
    titleKey = 'technicalService.cta.scientificWriting.title'
    descKey = 'technicalService.cta.scientificWriting.desc'
  } else if (isDataSecurity) {
    titleKey = 'technicalService.cta.dataSecurity.title'
    descKey = 'technicalService.cta.dataSecurity.desc'
  }

  // Hide on AI/ML overview page, applied AI/ML page, and data-security page
  if (isAiMlOverview || isAppliedAiMl || isDataSecurity) {
    return null
  }

  // Special CTA text for AI/ML sub-pages
  const aiMlCtaKey = isTargetIdentification ? 'targetId' :
                     isSurvivalRisk ? 'survivalRisk' :
                     isBiomarkerDiscovery ? 'biomarkerDiscovery' :
                     isPreclinicalBiomarker ? 'preclinicalBiomarker' :
                     isTranslationalPkPd ? 'translationalPkPd' :
                     isLeadOptimization ? 'leadOptimization' :
                     isHitDiscovery ? 'hitDiscovery' :
                     isClinicalResearch ? 'clinicalResearch' :
                     isPatientStratification ? 'clinicalResearch' :
                     isCohortReanalysis ? 'clinicalResearch' : null

  if (aiMlCtaKey) {
    return (
      <div className="bio-card-tinted rounded-2xl p-6 sm:p-8 border border-primary/20 flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left mt-16">
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white shadow-soft flex items-center justify-center text-primary flex-shrink-0">
          <span className="material-symbols-outlined text-3xl">contact_support</span>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold text-text-main mb-2 break-words">{t(`technicalService.cta.${aiMlCtaKey}.title`)}</h3>
          <p className="text-sm text-text-muted mb-4 break-words">{t(`technicalService.cta.${aiMlCtaKey}.desc`)}</p>
          <div className="flex flex-wrap gap-3 justify-center sm:justify-start">
            <a href="/contact" className="btn-primary px-5 py-2 text-xs flex items-center gap-2">
              {t('header.contactUs')} <span className="material-symbols-outlined text-sm">arrow_forward</span>
            </a>
            <a href="/service-process" className="px-5 py-2 text-xs font-bold text-text-muted hover:text-primary transition-colors flex items-center gap-1.5">
              {t('header.serviceProcess')} <span className="material-symbols-outlined text-sm">arrow_forward</span>
            </a>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bio-card-tinted rounded-2xl p-6 sm:p-8 border border-primary/20 flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left mt-16">
      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white shadow-soft flex items-center justify-center text-primary flex-shrink-0">
        <span className="material-symbols-outlined text-3xl">contact_support</span>
      </div>
      <div className="flex-1 min-w-0">
        {isDrugDiscoveryAiMl ? (
          <>
            {t('technicalService.cta.aiMl.title') && (
              <h3 className="font-display font-bold text-text-main mb-2 break-words">{t('technicalService.cta.aiMl.title')}</h3>
            )}
            <p className="text-sm text-text-muted mb-4 break-words">{t('technicalService.cta.aiMl.desc')}</p>
          </>
        ) : (
          <>
            <h3 className={`font-display font-bold text-text-main break-words ${descKey ? 'mb-2' : 'mb-8'}`}>{t(titleKey)}</h3>
            {descKey ? (
              isDataSecurity ? (
                <p className="text-sm text-text-muted mb-4 break-words" dangerouslySetInnerHTML={{ __html: t(descKey) }}></p>
              ) : (
                <p className="text-sm text-text-muted mb-4 break-words">{t(descKey)}</p>
              )
            ) : null}
          </>
        )}
        <div className="flex flex-wrap gap-3 justify-center sm:justify-start">
          {!isDataSecurity && (
            <a href="/contact" className="btn-primary px-5 py-2 text-xs flex items-center gap-2">
              {t('technicalService.cta.requestConsultation')} <span className="material-symbols-outlined text-sm">arrow_forward</span>
            </a>
          )}
          {!isDataSecurity && (
            <a href="/service-process" className="px-5 py-2 text-xs font-bold text-text-muted hover:text-primary transition-colors flex items-center gap-1.5">
              {t('technicalService.cta.viewProcess')} <span className="material-symbols-outlined text-sm">arrow_forward</span>
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

export default ServiceCTA